import { useState, useRef } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useModal, useToast } from "@/comn/hooks";
import { Icon } from "@/comn/components";
import OtpModal from "./OtpModal";
import { useActivateAccount } from "../authentication/useActivateAccount";
import { useNavigate } from "react-router-dom";

const MAX_ATTEMPTS = 3;

const ActivateAccount = ({ embedded = false }) => {

    const { t } = useTranslation();
    const modal = useModal();
    const toast = useToast();

    const imgs = { imgLock: `${process.env.PUBLIC_URL}/imgs/img-lock.png` };

    const { form, setForm, verifyUser, verifyOtp } = useActivateAccount();
    const { usrId, usrEmlAddr, isLcm } = form;

    const attemptsLeftRef = useRef(MAX_ATTEMPTS);
    const lockedRef = useRef(false);
    const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
    const [locked, setLocked] = useState(false);

    const navigate = useNavigate();

    const setOtpState = (attempts: number, isLocked: boolean) => {

        attemptsLeftRef.current = attempts;
        lockedRef.current = isLocked;
        setAttemptsLeft(attempts);
        setLocked(isLocked);

    };

    const otpSubmitRef = useRef<(() => void) | null>(null);

    const openOtpModal = () => {

        modal.openModal({
            size: "xs",
            close: false,
            topClose: true,
            content: (
                <OtpModal
                    getAttemptsLeft={() => attemptsLeftRef.current}
                    getLocked={() => lockedRef.current}
                    onSubmitReady={(submitFn) => {
                        otpSubmitRef.current = submitFn;
                    }}

                    onSubmit={async (otp) => {
                        try {

                            await verifyOtp(otp);
                            modal.closeModal();

                            setTimeout(() => {
                                modal.openModal({
                                    content: "Account activated successfully. Please login.",
                                    size: "xs",
                                    close: true,
                                    onClose: () => {
                                        process.env.REACT_APP_SYSTEM_GROUP === "tanesw"
                                            ? navigate("/ptle")
                                            : navigate("/login");
                                    },
                                    onConfirm: () => {
                                        process.env.REACT_APP_SYSTEM_GROUP === "tanesw"
                                            ? navigate("/ptle")
                                            : navigate("/login");
                                    },
                                });
                            }, 100);

                        } catch (error: any) {

                            const next = attemptsLeftRef.current - 1;
                            const isLocked = next <= 0;

                            setOtpState(next, isLocked);
                            toast.showToast({
                                type: "error",
                                content: error?.response?.data?.errorMessage || "Invalid OTP",
                                uniqueId: "otp_invalid",
                            });

                        }
                    }}



                    onResend={async () => {
                        const ok = await verifyUser();
                        if (ok) setOtpState(MAX_ATTEMPTS, false);
                    }}
                />
            ),
        });

    };

    const [isLoading, setIsLoading] = useState(false);

    const handleActivateClick = async () => {

        setIsLoading(true);
        const ok = await verifyUser();
        setIsLoading(false);

        if (ok) {
            setOtpState(MAX_ATTEMPTS, false);
            openOtpModal();
        }

    };

    return (
        <div className="w-full flex justify-center relative">
            <img
                src={imgs["imgLock"]}
                width={"700px"}
                height={"auto"}
                className="grayscale opacity-10 absolute top-[100px] -left-[300px] -z-[1] hidden md:block lg:block"
                alt=""
            />
            <div className="w-full flex justify-center mt-[70px]">
                <div className="w-full md:w-[520px] lg:w-[520px]">
                    <form
                        className={classNames(
                            "find-body p-[40px] flex flex-col gap-[70px]",
                            embedded ? "pt-[40px]" : "rounded-b-[30px] pt-[70px] bg-card shadow-lg",
                        )}
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <div className="find-body-min flex flex-col gap-[14px]">
                            {/* LCM checkbox — tanesw only, same as FindPassword */}
                            {process.env.REACT_APP_SYSTEM_GROUP === "tanesw" && (
                                <div className="h-12 rounded border border-blue flex items-center px-[16px] gap-[5px] text-blue">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            hidden
                                            className="peer"
                                            type="checkbox"
                                            checked={isLcm}
                                            onChange={(e) => setForm((prev) => ({ ...prev, isLcm: e.target.checked }))}
                                        />
                                        <div className="border rounded-full border-blue w-4 h-4 flex items-center justify-center [&_*]:peer-checked:block">
                                            <Icon className="hidden" icon="check" size="xs" />
                                        </div>
                                        License applicants, please select.
                                    </label>
                                </div>
                            )}

                            {/* ID Input */}
                            <p className="flex gap-[5px]">
                                <label htmlFor="usrId" className="block w-[100px] text-sm pt-[8px]">
                                    ID
                                </label>
                                <span className="flex flex-col gap-[4px] w-full">
                                    <input
                                        id="usrId"
                                        type="text"
                                        className="text-sm input bg-transparent h-[36px] w-full"
                                        value={usrId}
                                        onChange={(e) => setForm((prev) => ({ ...prev, usrId: e.target.value }))}
                                    />
                                </span>
                            </p>

                            {/* Email Input */}
                            <p className="flex gap-[5px]">
                                <label htmlFor="usrEmlAddr" className="block w-[100px] text-sm pt-[8px]">
                                    Email
                                </label>
                                <span className="flex flex-col gap-[4px] w-full">
                                    <input
                                        id="usrEmlAddr"
                                        type="email"
                                        className="text-sm input bg-transparent h-[36px] w-full"
                                        value={usrEmlAddr}
                                        onChange={(e) => setForm((prev) => ({ ...prev, usrEmlAddr: e.target.value }))}
                                    />
                                </span>
                            </p>
                        </div>

                        {/* Activate Button */}
                        <div className="flex gap-[10px]">
                            <button
                                type="button"
                                className={classNames(
                                    "w-full rounded-[32px] h-[60px] text-base bg-primary-fill-5 border border-primary text-primary dark:text-white dark:border-white dark:hover:border-primary",
                                    "hover:bg-primary hover:text-white hover:border-primary",
                                    isLoading && "opacity-50 cursor-not-allowed",
                                )}
                                onClick={handleActivateClick}
                                disabled={isLoading}
                            >
                                {isLoading ? "Verifying..." : t("L_ACTIVATE_ACCOUNT")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ActivateAccount;
